import React, { useState, useEffect, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { 
  PlusIcon, 
  PlayIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  DocumentDuplicateIcon,
  CreditCardIcon,
  ChartBarIcon,
  ShieldCheckIcon,
  UserPlusIcon,
  CalendarDaysIcon,
  EllipsisHorizontalIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { useAuth } from '../../contexts/AuthContext'
import RuleDryRunModal from './RuleDryRunModal'
import { RULE_CATEGORIES, CATEGORY_GROUPS, getCategoryByValue } from '../../config/categories'
import { formatProcessingCategoryLabel } from '../../utils/processingCategory'
import { formatDataSourceLabel } from '../../utils/ruleDataSource'

const CATEGORY_COLOR_CLASSES = {
  red: 'bg-red-50 text-red-700 border-red-200',
  orange: 'bg-orange-50 text-orange-700 border-orange-200',
  yellow: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  green: 'bg-green-50 text-green-700 border-green-200',
  blue: 'bg-blue-50 text-blue-700 border-blue-200',
  purple: 'bg-purple-50 text-purple-700 border-purple-200',
}

const TYPE_FILTER_OPTIONS = [
  { value: 'transaction', label: 'Transaction (meter / combined)' },
  { value: 'session', label: 'Session (gaming analytics)' },
  { value: 'onboarding', label: 'Onboarding (membership)' },
  { value: 'cumulative', label: 'Cumulative' },
  { value: 'compliance', label: 'Compliance' },
  { value: 'ongoing', label: 'Ongoing' },
]

function RuleActionsMenu({
  rule,
  activeTab,
  canMutate,
  onDryRun,
  onToggleStatus,
  onDuplicate,
  onAssignVenues,
  onSubmit,
  onMoveToDraft,
  onDelete,
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    if (!open) return
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  const close = () => setOpen(false)

  const items = []
  if (activeTab === 'published' && rule.enabled) {
    items.push({ key: 'dry-run', label: 'Run on trading day', action: () => { onDryRun(); close() } })
  }
  if (activeTab === 'draft') {
    items.push({ key: 'dry-run', label: 'Dry run', action: () => { onDryRun(); close() } })
  }
  if (canMutate) {
    items.push({
      key: 'edit',
      label: 'Edit',
      href: `/monitor-safe/builder/combined?id=${encodeURIComponent(rule.id)}`,
    })
  }
  if (canMutate && activeTab === 'published') {
    items.push({
      key: 'pause',
      label: rule.enabled ? 'Pause rule' : 'Resume rule',
      action: () => { onToggleStatus(); close() },
    })
  }
  if (canMutate) {
    items.push({ key: 'venues', label: 'Assign venues', action: () => { onAssignVenues(); close() } })
    items.push({ key: 'duplicate', label: 'Duplicate', action: () => { onDuplicate(); close() } })
    if (activeTab === 'draft') {
      items.push({ key: 'submit', label: 'Submit for approval', action: () => { onSubmit(); close() } })
    } else {
      items.push({ key: 'draft', label: 'Move to draft', action: () => { onMoveToDraft(); close() } })
    }
    items.push({
      key: 'delete',
      label: 'Delete',
      action: () => { onDelete(); close() },
      destructive: true,
    })
  }

  if (items.length === 0) return null

  return (
    <div className="relative inline-block text-left" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
        aria-label={`Actions for ${rule.name}`}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <EllipsisHorizontalIcon className="h-5 w-5" />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 z-[9999] mt-2 w-64 bg-white border border-gray-300 rounded-lg shadow-2xl py-2 flex flex-col"
        >
          {items.map((item) =>
            item.href ? (
              <Link
                key={item.key}
                role="menuitem"
                to={item.href}
                className="block w-full text-left px-5 py-3 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                onClick={close}
              >
                {item.label}
              </Link>
            ) : (
              <button
                key={item.key}
                type="button"
                role="menuitem"
                className={`w-full text-left px-5 py-3 text-sm hover:bg-gray-100 transition-colors ${
                  item.destructive ? 'text-red-600 font-medium' : 'text-gray-700'
                }`}
                onClick={item.action}
              >
                {item.label}
              </button>
            )
          )}
        </div>
      )}
    </div>
  )
}

function RulesList() {
  const { can } = useAuth()
  const navigate = useNavigate()
  const canMutateRules = can('rules', 'edit') || can('rules', 'manage')
  const [rules, setRules] = useState([])
  const [filteredRules, setFilteredRules] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [selectedType, setSelectedType] = useState('all')
  const [selectedSeverity, setSelectedSeverity] = useState('all')
  const [showFilters, setShowFilters] = useState(false)
  const [activeTab, setActiveTab] = useState('published') // 'published' | 'draft'
  const [isLoading, setIsLoading] = useState(false)
  const [loadError, setLoadError] = useState('')
  const [assignModal, setAssignModal] = useState({
    open: false,
    ruleId: null,
    venues: [],
    selected: new Set(),
    initialAssigned: new Set()
  })
  const [selectedRuleIds, setSelectedRuleIds] = useState(new Set())
  const [dryRunModal, setDryRunModal] = useState({ open: false, ruleIds: [], label: '', draftOnly: false })
  const importFileRef = useRef(null)
  const [importModal, setImportModal] = useState({ open: false, bundle: null, mode: 'create_only' })
  const [importBusy, setImportBusy] = useState(false)

  useEffect(() => {
    const fetchRules = async () => {
      setIsLoading(true)
      setLoadError('')
      try {
        const statusParam = activeTab === 'draft' ? 'draft' : 'published'
        const resp = await authFetch(getLocalApiUrl(`/api/rules/db?status=${statusParam}`))
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
        const json = await resp.json()
        if (json?.success && Array.isArray(json.data)) {
          const matchesDeprecatedSesE5 = (condsRaw) => {
            try {
              const conds = Array.isArray(condsRaw) ? condsRaw : JSON.parse(condsRaw || '[]')
              if (!Array.isArray(conds) || conds.length < 6) return false
              const toKey = (c) => (String(c.label || c.field || c.left || c.name || '').toLowerCase())
              const toOp = (c) => (String(c.operator || c.op || '').toLowerCase())
              const toVal = (c) => Number(c.value ?? c.right ?? c.threshold ?? c.val ?? NaN)
              const want = [
                { k: 'current period session count', op: 'greater_than', v: 10 },
                { k: 'previous period session count', op: 'greater_than', v: 10 },
                { k: 'current period avg turnover', op: 'greater_than', v: 100 },
                { k: 'previous period avg turnover', op: 'greater_than', v: 100 },
                { k: 'turnover change (%)', op: 'greater_than_or_equal', v: 200 },
                { k: 'win % change (pp)', op: 'less_than_or_equal', v: -200 }
              ]
              return want.every(w => conds.some(c => toKey(c).includes(w.k) && toOp(c) === w.op && toVal(c) === w.v))
            } catch { return false }
          }

          let normalized = json.data.map(r => ({
            id: r.rule_id || r.id,
            name: r.name,
            type: r.type,
            category: r.category,
            severity: (r.severity || '').toString().toUpperCase(),
            enabled: (r.enabled !== undefined ? !!r.enabled : true),
            description: r.description || '',
            rationale: r.rationale ?? '',
            rationale_source: r.rationale_source ?? null,
            processing_category: r.processing_category || null,
            triggerCount: r.triggerCount ?? 0,
            lastTriggered: r.lastTriggered ?? '—',
            createdAt: r.createdAt || '',
            _conditions: r.conditions
          }))

          normalized = normalized.filter(r => !(String(r.id).toUpperCase() === 'SES_E5_UNUSUAL_BEHAVIOUR' && matchesDeprecatedSesE5(r._conditions)))
          normalized = normalized.map(({ _conditions, ...rest }) => rest)

          setRules(normalized)
          setFilteredRules(normalized)
        } else {
          setRules([])
          setFilteredRules([])
        }
      } catch (err) {
        setLoadError('Failed to load rules from server')
        setRules([])
        setFilteredRules([])
      } finally {
        setIsLoading(false)
      }
    }
    fetchRules()
  }, [activeTab])

  useEffect(() => {
    setSelectedRuleIds(new Set())
  }, [activeTab])

  useEffect(() => {
    filterRules()
  }, [searchTerm, selectedCategory, selectedType, selectedSeverity, rules])

  const filterRules = () => {
    let filtered = rules
    if (searchTerm) {
      filtered = filtered.filter(rule => 
        rule.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (rule.description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        rule.id.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }
    if (selectedCategory !== 'all') {
      const cat = selectedCategory.toUpperCase()
      filtered = filtered.filter((rule) => String(rule.category || '').toUpperCase() === cat)
    }
    if (selectedType !== 'all') {
      const typ = selectedType.toLowerCase()
      filtered = filtered.filter((rule) => String(rule.type || '').toLowerCase() === typ)
    }
    if (selectedSeverity !== 'all') {
      const sev = selectedSeverity.toUpperCase()
      filtered = filtered.filter((rule) => String(rule.severity || '').toUpperCase() === sev)
    }
    setFilteredRules(filtered)
  }

  /** Published list: only flag empty/short rationale. Draft list: also flag backfill (submit requires author source). */
  const ruleNeedsRationaleBadge = (rule) => {
    const rat = String(rule.rationale ?? '').trim()
    const tooShort = rat.length < 20
    const isBackfill = rule.rationale_source === 'backfill'
    if (activeTab === 'draft') return tooShort || isBackfill
    return tooShort
  }

  const toggleRuleStatus = async (ruleId) => {
    try {
      setRules(rules.map(rule => 
        rule.id === ruleId ? { ...rule, enabled: !rule.enabled } : rule
      ))
      toast.success('Rule status updated')
    } catch (error) {
      toast.error('Failed to update rule status')
    }
  }

  const deleteRuleById = async (ruleId) => {
    const resp = await authFetch(getLocalApiUrl(`/api/rules/${encodeURIComponent(ruleId)}`), {
      method: 'DELETE',
    })
    const json = await resp.json().catch(() => ({}))
    if (!resp.ok || !json.success) {
      throw new Error(json.error || json.message || `Delete failed (${resp.status})`)
    }
  }

  const deleteRule = async (ruleId) => {
    if (!window.confirm('Are you sure you want to delete this rule?')) return
    try {
      await deleteRuleById(ruleId)
      setSelectedRuleIds((prev) => {
        const next = new Set(prev)
        next.delete(ruleId)
        return next
      })
      await reloadRulesList()
      toast.success('Rule deleted successfully')
    } catch (error) {
      toast.error(error?.message || 'Failed to delete rule')
    }
  }

  const deleteSelectedRules = async () => {
    const ids = [...selectedRuleIds]
    if (!ids.length) return
    if (!window.confirm(`Delete ${ids.length} selected rule(s)? This cannot be undone.`)) return

    setIsLoading(true)
    let deleted = 0
    let failed = 0
    try {
      for (const id of ids) {
        try {
          await deleteRuleById(id)
          deleted += 1
        } catch {
          failed += 1
        }
      }
      setSelectedRuleIds(new Set())
      await reloadRulesList()
      if (failed > 0) {
        toast.error(`Deleted ${deleted}, failed ${failed}`)
      } else {
        toast.success(`Deleted ${deleted} rule(s)`)
      }
    } finally {
      setIsLoading(false)
    }
  }

  const reloadRulesList = async (statusOverride) => {
    const statusParam = statusOverride || (activeTab === 'draft' ? 'draft' : 'published')
    const r = await authFetch(getLocalApiUrl(`/api/rules/db?status=${statusParam}`))
    const j2 = await r.json()
    if (j2?.success) {
      const normalized = (j2.data || []).map((rr) => ({
        id: rr.rule_id || rr.id,
        name: rr.name,
        type: rr.type,
        category: rr.category,
        severity: (rr.severity || '').toString().toUpperCase(),
        enabled: rr.enabled !== undefined ? !!rr.enabled : true,
        description: rr.description || '',
        rationale: rr.rationale ?? '',
        rationale_source: rr.rationale_source ?? null,
        processing_category: rr.processing_category || null,
        triggerCount: rr.triggerCount ?? 0,
        lastTriggered: rr.lastTriggered ?? '—',
        createdAt: rr.createdAt || ''
      }))
      setRules(normalized)
      setFilteredRules(normalized)
    }
  }

  /** Draft → submit for maker/checker (Pending Approvals). Does not publish directly. */
  const submitRuleForApproval = async (ruleId) => {
    try {
      const resp = await authFetch(getLocalApiUrl(`/api/rules/${encodeURIComponent(ruleId)}/submit`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ changelog: 'Submitted for publish approval' }),
      })
      const j = await resp.json().catch(() => ({}))
      if (!resp.ok || !j.success) throw new Error(j.error || 'Submit failed')
      if (j.data?.approval_warning) {
        toast.error(j.data.approval_warning)
      } else {
        toast.success('Submitted for approval — a checker can approve it under Pending Approvals')
      }
      await reloadRulesList()
    } catch (e) {
      toast.error(e?.message || 'Failed to submit rule for approval')
    }
  }

  /** Published → draft status (DB). */
  const moveRuleToDraft = async (ruleId) => {
    try {
      const resp = await authFetch(getLocalApiUrl(`/api/rules/${encodeURIComponent(ruleId)}/unpublish`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      })
      const j = await resp.json().catch(() => ({}))
      if (!resp.ok || !j.success) throw new Error(j.error || 'Failed to move to draft')
      toast.success('Moved to Draft')
      await reloadRulesList()
    } catch (e) {
      toast.error(e?.message || 'Failed to move rule to draft')
    }
  }

  const openAssignVenuesModal = async (ruleId) => {
    try {
      const [allRes, assignedRes] = await Promise.all([
        authFetch(getLocalApiUrl('/api/venues')),
        authFetch(getLocalApiUrl(`/api/rules/${encodeURIComponent(ruleId)}/venues`)),
      ])
      const allJson = allRes.ok ? await allRes.json() : {}
      const asgJson = assignedRes.ok ? await assignedRes.json() : {}
      const allVenues = Array.isArray(allJson.data) ? allJson.data : []
      const assigned = Array.isArray(asgJson.data) ? asgJson.data : []
      const assignedIds = new Set(assigned.map((x) => String(x.id)))
      setAssignModal({
        open: true,
        ruleId,
        venues: allVenues,
        selected: new Set(assignedIds),
        initialAssigned: new Set(assignedIds),
      })
    } catch {
      toast.error('Failed to load venues')
    }
  }

  const duplicateRule = async (rule) => {
    try {
      const resp = await authFetch(getLocalApiUrl(`/api/rules/${encodeURIComponent(rule.id)}`))
      const json = await resp.json().catch(() => ({}))
      if (!resp.ok || !json.success || !json.data) {
        throw new Error(json.error || 'Could not load the rule to copy')
      }
      const src = json.data
      const newId = `${String(src.type || 'RULE').toUpperCase().replace(/[^A-Z0-9]+/g, '_')}_${Date.now()}`
      const payload = {
        id: newId,
        name: `${src.name || rule.name || 'Rule'} (Copy)`,
        description: src.description || '',
        rationale: src.rationale || '',
        type: src.type,
        category: src.category,
        severity: src.severity,
        enabled: false,
        processing_category: src.processing_category,
        time_window_days: src.time_window_days,
        aggregation: src.aggregation,
        conditions: src.conditions,
        actions: src.actions,
        metadata: src.metadata,
      }
      const created = await authFetch(getLocalApiUrl('/api/rules'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      const createdJson = await created.json().catch(() => ({}))
      if (!created.ok || !createdJson.success) {
        throw new Error(createdJson.error || createdJson.message || 'Failed to duplicate rule')
      }
      toast.success('Rule copied to drafts')
      navigate(`/monitor-safe/builder/combined?id=${encodeURIComponent(newId)}`)
    } catch (error) {
      toast.error(error.message || 'Failed to duplicate rule')
    }
  }

  const toggleRuleSelection = (ruleId) => {
    setSelectedRuleIds((prev) => {
      const next = new Set(prev)
      if (next.has(ruleId)) next.delete(ruleId)
      else next.add(ruleId)
      return next
    })
  }

  const openDryRun = (ruleIds, label) => {
    setDryRunModal({ open: true, ruleIds, label, draftOnly: activeTab === 'draft' })
  }

  const exportRulesJson = async () => {
    const ids = selectedRuleIds.size > 0
      ? [...selectedRuleIds]
      : filteredRules.map((r) => r.id)
    if (!ids.length) {
      toast.error('No rules to export')
      return
    }
    try {
      const resp = await authFetch(getLocalApiUrl('/api/rules/export'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rule_ids: ids,
          status: activeTab === 'draft' ? 'draft' : 'published',
        }),
      })
      const json = await resp.json().catch(() => ({}))
      if (!resp.ok || !json.success) throw new Error(json.error || `Export failed (${resp.status})`)
      const bundle = json.data
      const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `paysafego-rules-${new Date().toISOString().slice(0, 10)}.json`
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
      const missing = bundle.missing_rule_ids?.length || 0
      toast.success(
        missing
          ? `Exported ${bundle.rule_count} rule(s) (${missing} id(s) not found)`
          : `Exported ${bundle.rule_count} rule(s)`
      )
    } catch (e) {
      toast.error(e?.message || 'Failed to export rules')
    }
  }

  const onImportFileChosen = async (event) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    try {
      const text = await file.text()
      const bundle = JSON.parse(text)
      if (bundle?.format !== 'paysafego.rules.v1') {
        throw new Error('Not a paysafego.rules.v1 bundle')
      }
      setImportModal({ open: true, bundle, mode: 'create_only' })
    } catch (e) {
      toast.error(e?.message || 'Invalid rules JSON file')
    }
  }

  const runImport = async () => {
    if (!importModal.bundle) return
    setImportBusy(true)
    try {
      const resp = await authFetch(getLocalApiUrl('/api/rules/import'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          bundle: importModal.bundle,
          mode: importModal.mode,
        }),
      })
      const json = await resp.json().catch(() => ({}))
      if (!resp.ok || !json.success) throw new Error(json.error || `Import failed (${resp.status})`)
      const { summary } = json.data || {}
      toast.success(
        `Import complete: ${summary?.created || 0} created, ${summary?.updated || 0} updated, ${summary?.skipped || 0} skipped, ${summary?.failed || 0} failed`
      )
      setImportModal({ open: false, bundle: null, mode: 'create_only' })
      setActiveTab('draft')
      await reloadRulesList('draft')
    } catch (e) {
      toast.error(e?.message || 'Failed to import rules')
    } finally {
      setImportBusy(false)
    }
  }

  const enabledFilteredIds = filteredRules.filter((r) => r.enabled).map((r) => r.id)

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'CRITICAL': return 'bg-red-100 text-red-800'
      case 'HIGH': return 'bg-orange-100 text-orange-800'
      case 'MEDIUM': return 'bg-yellow-100 text-yellow-800'
      case 'LOW': return 'bg-green-100 text-green-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getCategoryColor = (category) => {
    const meta = getCategoryByValue(String(category || '').toUpperCase())
    if (meta?.color && CATEGORY_COLOR_CLASSES[meta.color]) {
      return CATEGORY_COLOR_CLASSES[meta.color]
    }
    return 'bg-gray-50 text-gray-700 border-gray-200'
  }

  const getTypeIcon = (type) => {
    switch (type) {
      case 'transaction': return CreditCardIcon
      case 'session': return ChartBarIcon
      case 'cumulative': return ChartBarIcon
      case 'compliance': return ShieldCheckIcon
      case 'onboarding': return UserPlusIcon
      default: return DocumentDuplicateIcon
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Rules Management</h1>
          <p className="text-sm text-gray-500">Manage and monitor your club transaction rules</p>
        </div>
        {canMutateRules && (
          <Link to="/monitor-safe/builder/combined" className="btn-primary flex items-center">
            <PlusIcon className="h-5 w-5 mr-2" />
            Create Rule
          </Link>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card text-center">
          <div className="text-2xl font-bold text-blue-600">{rules.filter(r => r.enabled).length}</div>
          <div className="text-sm text-gray-500">Active Rules</div>
        </div>
        <div className="card text-center">
          <div className="text-2xl font-bold text-red-600">{rules.filter(r => r.severity === 'CRITICAL').length}</div>
          <div className="text-sm text-gray-500">Critical Rules</div>
        </div>
        <div className="card text-center">
          <div className="text-2xl font-bold text-green-600">{rules.reduce((sum, r) => sum + (Number(r.triggerCount)||0), 0)}</div>
          <div className="text-sm text-gray-500">Total Triggers</div>
        </div>
        <div className="card text-center">
          <div className="text-2xl font-bold text-purple-600">{rules.filter(r => (r.processing_category||'').toLowerCase() === 'cumulative').length}</div>
          <div className="text-sm text-gray-500">Cumulative Rules</div>
        </div>
      </div>

      {isLoading && <div className="card text-sm text-gray-600">Loading rules…</div>}
      {!isLoading && loadError && (
        <div className="card text-sm text-yellow-700 bg-yellow-50 border border-yellow-200">{loadError}</div>
      )}

      <div className="card">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex-1 min-w-0 max-w-lg">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search rules..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field pl-10 w-full"
              />
            </div>
          </div>
          <div className="flex items-center flex-wrap gap-2 md:gap-3 shrink-0">
            <button onClick={() => setShowFilters(!showFilters)} className="btn-secondary flex items-center">
              <FunnelIcon className="h-4 w-4 mr-2" />
              Filters
            </button>
            <button
              type="button"
              className="btn-secondary flex items-center text-sm text-red-700 border-red-200 hover:bg-red-50 disabled:opacity-50"
              disabled={selectedRuleIds.size === 0 || isLoading}
              onClick={deleteSelectedRules}
            >
              <TrashIcon className="h-4 w-4 mr-2" />
              {selectedRuleIds.size > 0 ? `Delete (${selectedRuleIds.size})` : 'Delete selected'}
            </button>
            <button
              type="button"
              className="btn-secondary flex items-center text-sm"
              disabled={selectedRuleIds.size === 0}
              onClick={() => openDryRun([...selectedRuleIds], `${selectedRuleIds.size} selected rule(s)`)}
            >
              <CalendarDaysIcon className="h-4 w-4 mr-2" />
              Run selected
            </button>
            {activeTab === 'published' && (
              <button
                type="button"
                className="btn-secondary flex items-center text-sm"
                disabled={enabledFilteredIds.length === 0}
                onClick={() => openDryRun(enabledFilteredIds, `All enabled (${enabledFilteredIds.length})`)}
              >
                <PlayIcon className="h-4 w-4 mr-2" />
                Run all enabled
              </button>
            )}
            <button
              type="button"
              className="btn-secondary flex items-center text-sm"
              disabled={filteredRules.length === 0}
              onClick={exportRulesJson}
            >
              <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
              {selectedRuleIds.size > 0 ? `Export (${selectedRuleIds.size})` : 'Export all'}
            </button>
            <button
              type="button"
              className="btn-secondary flex items-center text-sm"
              onClick={() => importFileRef.current?.click()}
            >
              <ArrowUpTrayIcon className="h-4 w-4 mr-2" />
              Import JSON
            </button>
            <input
              ref={importFileRef}
              type="file"
              accept="application/json,.json"
              className="hidden"
              onChange={onImportFileChosen}
            />
            <div className="text-sm text-gray-500">{filteredRules.length} of {rules.length} rules</div>
          </div>
        </div>

        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                <select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)} className="select-field">
                  <option value="all">All Categories</option>
                  {Object.entries(RULE_CATEGORIES).map(([groupKey, categories]) => (
                    <optgroup key={groupKey} label={CATEGORY_GROUPS[groupKey]}>
                      {categories.map((cat) => (
                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                <select value={selectedType} onChange={(e) => setSelectedType(e.target.value)} className="select-field">
                  <option value="all">All Types</option>
                  {TYPE_FILTER_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Severity</label>
                <select value={selectedSeverity} onChange={(e) => setSelectedSeverity(e.target.value)} className="select-field">
                  <option value="all">All Severities</option>
                  <option value="CRITICAL">Critical</option>
                  <option value="HIGH">High</option>
                  <option value="MEDIUM">Medium</option>
                  <option value="LOW">Low</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="card overflow-visible">
        <div className="flex gap-4 px-4 border-b border-gray-200">
          <button
            type="button"
            className={`px-1 py-3 text-sm ${activeTab === 'published' ? 'text-psg-teal border-b-2 border-psg-teal font-semibold -mb-px' : 'text-gray-600 hover:text-gray-900'}`}
            onClick={() => setActiveTab('published')}
          >
            Published
          </button>
          <button
            type="button"
            className={`px-1 py-3 text-sm ${activeTab === 'draft' ? 'text-psg-teal border-b-2 border-psg-teal font-semibold -mb-px' : 'text-gray-600 hover:text-gray-900'}`}
            onClick={() => setActiveTab('draft')}
          >
            Draft
          </button>
        </div>
        <div className="overflow-x-auto overflow-y-visible">
          <table className="min-w-full w-full table-fixed divide-y divide-gray-200">
            <colgroup>
              <col className="w-[36%]" />
              <col className="w-[13%]" />
              <col className="w-[9%]" />
              <col className="w-[9%]" />
              <col className="w-[11%]" />
              <col className="w-[10%]" />
            </colgroup>
            <thead className="bg-gray-50">
              <tr>
                <th className="pl-3 pr-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center gap-2 min-w-0">
                    <input
                      type="checkbox"
                      className="shrink-0"
                      aria-label="Select all visible rules"
                      checked={filteredRules.length > 0 && filteredRules.every((r) => selectedRuleIds.has(r.id))}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedRuleIds(new Set(filteredRules.map((r) => r.id)))
                        } else {
                          setSelectedRuleIds(new Set())
                        }
                      }}
                    />
                    <span>Rule</span>
                  </div>
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Source & grain</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Severity</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Activity</th>
                <th className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredRules.map((rule) => (
                <tr key={rule.id} className="hover:bg-gray-50">
                  <td className="pl-3 pr-2 py-4 max-w-0">
                    <div className="flex items-start gap-2 min-w-0">
                      <input
                        type="checkbox"
                        className="shrink-0 mt-1"
                        checked={selectedRuleIds.has(rule.id)}
                        onChange={() => toggleRuleSelection(rule.id)}
                        aria-label={`Select ${rule.name}`}
                      />
                      {(() => { const Icon = getTypeIcon(rule.type); return (<Icon className="w-5 h-5 text-gray-500 shrink-0 mt-0.5" />) })()}
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-gray-900 flex items-center gap-2 flex-wrap">
                          <span className="truncate" title={rule.name}>{rule.name}</span>
                          {/* S3 — draft: backfill must be re-saved as author before submit. Published: only empty/short. */}
                          {ruleNeedsRationaleBadge(rule) && (
                            <>
                              <span
                                title={
                                  String(rule.rationale ?? '').trim().length < 20
                                    ? 'Rationale missing — rule cannot be submitted until a rationale of at least 20 characters is authored.'
                                    : 'Rationale was backfilled from description — open the rule and save an authored rationale before submit.'
                                }
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-100 text-amber-800 border border-amber-200 shrink-0"
                              >
                                ⚠ Needs rationale
                              </span>
                              <Link
                                to={`/monitor-safe/builder/combined?id=${encodeURIComponent(rule.id)}&focus=rationale`}
                                className="text-[11px] font-semibold text-amber-900 hover:text-amber-950 underline underline-offset-2 shrink-0"
                              >
                                Add rationale
                              </Link>
                            </>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 truncate font-mono" title={rule.id}>{rule.id}</div>
                        {rule.description ? (
                          <div className="text-xs text-gray-400 mt-1 line-clamp-2" title={rule.description}>
                            {rule.description}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap">
                    <div className="space-y-1">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        {formatDataSourceLabel(rule.type)}
                      </span>
                      <div>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-800 border border-indigo-100">
                          {formatProcessingCategoryLabel(rule.processing_category) || 'Per session'}
                        </span>
                      </div>
                      <div>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getCategoryColor(rule.category)}`}>
                        {getCategoryByValue(String(rule.category || '').toUpperCase())?.label || rule.category || '—'}
                      </span>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSeverityColor(rule.severity)}`}>{rule.severity}</span>
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className={`w-2 h-2 rounded-full mr-2 ${rule.enabled ? 'bg-green-400' : 'bg-gray-400'}`}></div>
                      <span className={`text-sm ${rule.enabled ? 'text-green-600' : 'text-gray-500'}`}>{rule.enabled ? 'Active' : 'Inactive'}</span>
                    </div>
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div>
                      <div className="font-medium text-gray-900">{rule.triggerCount} triggers</div>
                      <div className="text-xs truncate" title={`Last: ${rule.lastTriggered}`}>Last: {rule.lastTriggered}</div>
                    </div>
                  </td>
                  <td className="px-2 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <RuleActionsMenu
                      rule={rule}
                      activeTab={activeTab}
                      canMutate={canMutateRules}
                      onDryRun={() => openDryRun([rule.id], rule.name)}
                      onToggleStatus={() => toggleRuleStatus(rule.id)}
                      onDuplicate={() => duplicateRule(rule)}
                      onAssignVenues={() => openAssignVenuesModal(rule.id)}
                      onSubmit={() => submitRuleForApproval(rule.id)}
                      onMoveToDraft={() => moveRuleToDraft(rule.id)}
                      onDelete={() => deleteRule(rule.id)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredRules.length === 0 && (
          <div className="text-center py-12">
            <DocumentDuplicateIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No rules found</h3>
            <p className="text-gray-500 mb-4">
              {searchTerm || selectedCategory !== 'all' || selectedType !== 'all' || selectedSeverity !== 'all'
                ? 'Try adjusting your search or filter criteria.'
                : 'Get started by creating your first rule.'}
            </p>
            {!searchTerm && selectedCategory === 'all' && selectedType === 'all' && selectedSeverity === 'all' && canMutateRules && (
              <Link to="/monitor-safe/builder/combined" className="btn-primary inline-flex items-center">
                <PlusIcon className="h-5 w-5 mr-2" />
                Create Your First Rule
              </Link>
            )}
          </div>
        )}
      </div>

      {assignModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-30 z-50 flex">
          <div className="m-auto w-full max-w-2xl bg-white rounded shadow-lg">
            <div className="p-4 border-b flex items-center justify-between">
              <div className="text-base font-medium">Assign Venues to Rule</div>
              <button className="text-gray-600" onClick={()=>setAssignModal({ open:false, ruleId:null, venues:[], selected:new Set(), initialAssigned:new Set() })}>Close</button>
            </div>
            <div className="p-4 space-y-3">
              <div className="text-sm text-gray-700">Rule ID: <span className="font-mono">{assignModal.ruleId}</span></div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {(assignModal.venues||[]).map(v => (
                  <label key={v.id} className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={assignModal.selected.has(String(v.id))} onChange={(e)=>{
                      setAssignModal(prev=>{ const next = new Set(prev.selected); if (e.target.checked) next.add(String(v.id)); else next.delete(String(v.id)); return { ...prev, selected: next } })
                    }} />
                    <span>{v.name ?? v.venue_id ?? v.code ?? `Venue ${v.id}`}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="p-4 border-t flex justify-end gap-2">
              <button className="btn-secondary" onClick={()=>setAssignModal({ open:false, ruleId:null, venues:[], selected:new Set(), initialAssigned:new Set() })}>Cancel</button>
              <button className="btn-primary" onClick={async()=>{
                try {
                  const initial = assignModal.initialAssigned || new Set()
                  const next = assignModal.selected
                  const toAdd = [...next].filter(x => !initial.has(x))
                  const toRemove = [...initial].filter(x => !next.has(x))
                  for (const vid of toAdd) { await authFetch(getLocalApiUrl(`/api/rules/${encodeURIComponent(assignModal.ruleId)}/venues`), { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ venue_id: vid }) }) }
                  for (const vid of toRemove) { await authFetch(getLocalApiUrl(`/api/rules/${encodeURIComponent(assignModal.ruleId)}/venues/${encodeURIComponent(vid)}`), { method:'DELETE' }) }
                  setAssignModal({ open:false, ruleId:null, venues:[], selected:new Set(), initialAssigned:new Set() })
                  toast.success('Assignments updated')
                } catch { toast.error('Failed to update assignments') }
              }}>Save</button>
            </div>
          </div>
        </div>
      )}

      {importModal.open && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-24 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Import rules</h3>
            <p className="text-sm text-gray-600 mb-4">
              {importModal.bundle?.rule_count ?? importModal.bundle?.rules?.length ?? 0} rule(s) in file.
              Imports always land in <strong>Draft</strong>.
            </p>
            <label className="block text-xs text-gray-600 mb-1">Import mode</label>
            <select
              className="select-field w-full mb-4"
              value={importModal.mode}
              onChange={(e) => setImportModal((prev) => ({ ...prev, mode: e.target.value }))}
            >
              <option value="create_only">Create only — skip existing rule IDs</option>
              <option value="upsert">Upsert — overwrite existing as draft</option>
              <option value="copy">Copy — new IDs when rule already exists</option>
            </select>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="btn-secondary"
                disabled={importBusy}
                onClick={() => setImportModal({ open: false, bundle: null, mode: 'create_only' })}
              >
                Cancel
              </button>
              <button type="button" className="btn-primary" disabled={importBusy} onClick={runImport}>
                {importBusy ? 'Importing…' : 'Import'}
              </button>
            </div>
          </div>
        </div>
      )}

      <RuleDryRunModal
        open={dryRunModal.open}
        ruleIds={dryRunModal.ruleIds}
        ruleLabel={dryRunModal.label}
        draftOnly={dryRunModal.draftOnly}
        onClose={() => setDryRunModal({ open: false, ruleIds: [], label: '', draftOnly: false })}
      />
    </div>
  )
}

export default RulesList
